import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

// Lines kept in memory while following live; older ones drop off the top.
export const MAX_LIVE_LINES = 5000

// The query string for a search or a download, from the page's filters. The
// names match ServerLogs::Filters.from_params.
export function searchParams({ q, regex, levels = [], since, status, slowRequest, slowQuery, limit }) {
  return {
    q,
    regex: regex ? 'true' : undefined,
    levels: levels.join(','),
    since,
    status,
    slow_request: slowRequest,
    slow_query: slowQuery,
    limit,
  }
}

// Every line passing the filters, streamed by ServerLogDownloadsController.
export function downloadUrl(filters = {}) {
  const query = new URLSearchParams(
    Object.entries(searchParams({ ...filters, limit: undefined }))
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString()
  return query ? `/api/v1/admin/server_logs/download?${query}` : '/api/v1/admin/server_logs/download'
}

// The Rails log viewer at /admin/server_logs. Logs change every second, so
// unlike the other page stores there is no TTL cache: every visit reads fresh.
// See app/controllers/api/v1/admin/server_logs_controller.rb.
export const useAdminServerLogsStore = defineStore('adminServerLogs', {
  state: () => ({
    lines: [],
    file: null,
    size: 0,
    offset: null,       // byte offset to follow from; only set in tail mode
    mode: 'tail',       // 'tail' | 'search' | 'request'
    requestId: null,
    truncated: false,
    timedOut: false,
    loading: false,
    error: null,        // page level error (skeleton → error state)
    searchError: null,  // a bad regex, shown next to the search box
  }),

  getters: {
    // Development logs use the plain formatter, which has no level or time
    // prefix, so the level and time range filters do not apply to them.
    hasLevels: (state) => state.lines.some((line) => line.level),
  },

  actions: {
    async get(url, params = {}) {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      ).toString()
      const { axiosRequest } = useRequest()
      return axiosRequest('GET', query ? `${url}?${query}` : url, {}, false)
    },

    applyMeta(data) {
      this.file = data.file
      this.size = data.size
      this.truncated = !!data.truncated
      this.timedOut = !!data.timed_out
    },

    async fetchTail(limit) {
      await this.load(async () => {
        const res = await this.get('/api/v1/admin/server_logs', { limit })
        if (res.js_error || !res.response.success) return false
        this.applyMeta(res.response.data)
        this.lines = res.response.data.lines
        this.offset = res.response.data.offset
        this.mode = 'tail'
        this.requestId = null
        return true
      })
    },

    // One live poll. Returns the number of new lines so the page knows
    // whether to scroll. Failures are silent; the next poll tries again.
    async follow() {
      if (this.mode !== 'tail' || this.offset === null) return 0
      try {
        const res = await this.get('/api/v1/admin/server_logs', { offset: this.offset })
        if (res.js_error || !res.response.success || this.mode !== 'tail') return 0
        const data = res.response.data
        this.file = data.file
        this.size = data.size
        this.offset = data.offset
        if (data.reset) {
          this.lines = data.lines
        } else if (data.lines.length) {
          this.lines = this.lines.concat(data.lines).slice(-MAX_LIVE_LINES)
        }
        return data.lines.length
      } catch {
        return 0
      }
    },

    async search(filters) {
      this.searchError = null
      await this.load(async () => {
        const res = await this.get('/api/v1/admin/server_logs/search', searchParams(filters))
        // A bad regex (422) is the searcher's mistake, not a broken page.
        const invalid = res.js_error ? res.error?.response?.status === 422 : !res.response.success
        if (invalid) {
          this.searchError = (res.js_error ? res.error.response.data?.message : res.response.message) || 'Search failed.'
          return true
        }
        if (res.js_error) return false
        this.applyMeta(res.response.data)
        this.lines = res.response.data.lines
        this.offset = null
        this.mode = 'search'
        this.requestId = null
        return true
      })
    },

    async fetchRequest(requestId) {
      await this.load(async () => {
        const res = await this.get(`/api/v1/admin/server_logs/requests/${encodeURIComponent(requestId)}`)
        if (res.js_error || !res.response.success) return false
        this.applyMeta(res.response.data)
        this.lines = res.response.data.lines
        this.offset = null
        this.mode = 'request'
        this.requestId = requestId
        return true
      })
    },

    async load(fn) {
      this.loading = true
      this.error = null
      try {
        if (!(await fn())) this.error = 'Failed to read the log file.'
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },
  },
})
